import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CheckboxField } from '@/components/form/fields/checkbox/Checkbox';
import { TextAndIconField } from '@/components/form/fields/text/TextAndIconInput';
import { TextField } from '@/components/form/fields/text/TextInput';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { CategoryIcons } from '@/components/icons/CategoryIcons';
import { useCreateCategory, useGetRateCards } from '@/services/api/hooks/catalog';
import { zodResolver } from '@hookform/resolvers/zod';
import { FC, useMemo, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { z } from 'zod';
import { Form } from '../../../components/form/Form';
import { Modal } from '../../../components/modal/Modal';
import { ModalProps } from '../../../components/modal/useModal';
import { Card } from '../../../components/sections/Card';
import TenantRestrictionTree from '@/components/org/TenantRestrictionTree';
import { useProcessOrgTreeForTenantRestrictions } from '@/components/org/useProcessOrgTreeForTenantRestrictions';
import { ToggleInput } from '@/components/form/fields/toggle/Toggle';

const DirectionalLogisticsFields = z.object({
  averageWeightKg: z.coerce.number().optional(),
  dimensionalWeightKg: z.coerce.number().optional(),
  packingEfficiencyPercent: z.coerce.number().optional(),
  overrideRateCardId: z.string().optional(),
  itemProcessingRateCardId: z.string().optional()
});

const CreateCategoryFormFields = z.object({
  name: z.object({
    text: z.string(),
    iconId: z.string()
  }),
  isMainDeviceCategory: z.boolean().optional(),
  isSerialized: z.boolean().optional(),
  hideFromGenerics: z.boolean().optional(),
  deploy: DirectionalLogisticsFields.optional(),
  return: DirectionalLogisticsFields.optional()
}).superRefine((data, ctx) => {
  if (!data.hideFromGenerics) {
    if (data.deploy && (data.deploy.averageWeightKg || data.deploy.dimensionalWeightKg)) {
      if (data.deploy.packingEfficiencyPercent === undefined || data.deploy.packingEfficiencyPercent === null || data.deploy.packingEfficiencyPercent < 1 || data.deploy.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['deploy', 'packingEfficiencyPercent']
        });
      }
    }
    if (data.return && (data.return.averageWeightKg || data.return.dimensionalWeightKg)) {
      if (data.return.packingEfficiencyPercent === undefined || data.return.packingEfficiencyPercent === null || data.return.packingEfficiencyPercent < 1 || data.return.packingEfficiencyPercent > 100) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Packing Efficiency is required (1-100) for physical categories',
          path: ['return', 'packingEfficiencyPercent']
        });
      }
    }
  }
});

type CreateCategoryFormFields = z.infer<typeof CreateCategoryFormFields>;

export const CreateCategoryModal: FC<ModalProps> = props => {
  const createCategory = useCreateCategory();
  const methods = useForm<CreateCategoryFormFields>({ resolver: zodResolver(CreateCategoryFormFields) });
  
  const orgTree = useProcessOrgTreeForTenantRestrictions();
  const [isInternalOnly, setIsInternalOnly] = useState(false);
  
  const [rateCardsResp, { isLoading: isRateCardsLoading }] = useGetRateCards();
  const overrideRateCards = useMemo(() => {
    if (isRateCardsLoading) return [];
    return rateCardsResp?.items?.filter(r => r.propertyBag?.isExclusiveOverride === true || String(r.propertyBag?.isExclusiveOverride) === 'true').map(r => ({ id: r.id, name: r.name })) || [];
  }, [rateCardsResp, isRateCardsLoading]);

  const itemProcessingRateCards = useMemo(() => {
    if (isRateCardsLoading) return [];
    return rateCardsResp?.items?.filter(r => r.propertyBag?.isItemRateOverride === true || String(r.propertyBag?.isItemRateOverride) === 'true').map(r => ({ id: r.id, name: r.name })) || [];
  }, [rateCardsResp, isRateCardsLoading]);

  const deployFallback = useMemo(() => {
    const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'DeployInventory');
    return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
  }, [rateCardsResp]);

  const returnFallback = useMemo(() => {
    const defaultCard = rateCardsResp?.items?.find(r => r.propertyBag?.itemRateForRequestType === 'OffboardUser');
    return { id: '', name: defaultCard ? `Default (${defaultCard.name})` : 'Default' };
  }, [rateCardsResp]);

  const onSubmit = async (data: CreateCategoryFormFields) => {
    const hiddenFromTenantIds = isInternalOnly
      ? ["00000000-0000-0000-0000-000000000000"]
      : orgTree.reduce((ids: string[], msp) => {
          if (msp.checked) {
            ids.push(msp.mspId);
          }
          msp.customers.forEach(customer => {
            if (customer.checked) {
              ids.push(customer.customerId);
            }
          });
          return ids;
        }, []);

    await createCategory.mutateAsync({
      name: data.name.text,
      description: data.name.iconId,
      isMainDevice: data.isMainDeviceCategory || false,
      hiddenFromTenantIds,
      isSerialized: data.isSerialized || false,
      hideFromGenerics: data.hideFromGenerics || false,
      logisticsConfig: {
        deploy: data.deploy,
        return: data.return
      }
    });
    props.close();
  };

  return (
    <Modal className="md:max-w-2xl" {...props}>
      <Card className="w-full p-8 max-h-[90vh] overflow-y-auto">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Create category</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col gap-3 py-2" methods={methods} onSubmit={onSubmit}>
          <TextAndIconField id="name" label="Name" icons={CategoryIcons} />
          
          <div className="flex flex-col gap-2 mt-2">
            <Controller
              name="isMainDeviceCategory"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Main Device Category</span>
                </label>
              )}
            />
            <Controller
              name="hideFromGenerics"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Hide from Generic Returns List</span>
                </label>
              )}
            />
            <Controller
              name="isSerialized"
              control={methods.control}
              render={({ field }) => (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={field.value} onChange={e => field.onChange(e.target.checked)} className="w-4 h-4 text-primary-700" />
                  <span className="text-sm font-medium text-gray-700">Serialised Return Required</span>
                </label>
              )}
            />
          </div>
          
          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Deployment Logistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <TextField id="deploy.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="deploy.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              <Controller
                name="deploy.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="deploy.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
              <Controller
                name="deploy.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Deployment Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="deploy.itemProcessingRateCardId"
                      options={[deployFallback, ...itemProcessingRateCards]}
                      placeholder={deployFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
            </div>
          </div>

          <div className="space-y-4 mt-4 border-t pt-4">
            <h2 className="text-lg font-medium">Return Logistics</h2>
            <div className="grid grid-cols-2 gap-4">
              <TextField id="return.averageWeightKg" label="Average Weight (kg)" onlyDigits={true} />
              <TextField id="return.dimensionalWeightKg" label="Dimensional Weight (kg)" onlyDigits={true} />
              <TextField id="return.packingEfficiencyPercent" label="Packing Efficiency (%)" onlyDigits={true} />
              <Controller
                name="return.overrideRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Override Logistics With (Optional)</label>
                    <SelectInput
                      id="return.overrideRateCardId"
                      options={[{ id: '', name: 'None' }, ...overrideRateCards]}
                      placeholder="None"
                      value={overrideRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
              <Controller
                name="return.itemProcessingRateCardId"
                control={methods.control}
                render={({ field }) => (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Return Processing Rate Card (Optional)</label>
                    <SelectInput
                      id="return.itemProcessingRateCardId"
                      options={[returnFallback, ...itemProcessingRateCards]}
                      placeholder={returnFallback.name}
                      value={itemProcessingRateCards.find(r => r.id === field.value) || null}
                      onChange={(val: any) => field.onChange(val?.id || undefined)}
                    />
                  </div>
                )}
              />
            </div>
          </div>
          
          <div className="space-y-1 border-t pt-4">
            <h2 className="text-lg font-medium">Hide from Tenants</h2>
            <div className="flex items-center gap-3 py-2">
              <ToggleInput
                id="isInternalOnly"
                value={isInternalOnly}
                onChange={setIsInternalOnly}
              />
              <span className="text-sm font-medium text-gray-700">Internal Only / Hide from all tenants</span>
            </div>
            {!isInternalOnly && (
              <>
                <p className="text-sm text-gray-500">Select MSPs or Customers that should NOT see this category.</p>
                <TenantRestrictionTree data={orgTree} />
              </>
            )}
          </div>

          <Button
            loading={createCategory.isLoading}
            className="w-fit place-self-end mt-4"
            variant={ButtonVariant.Primary}
            type="submit"
          >
            Create
          </Button>
        </Form>
      </Card>
    </Modal>
  );
};
